/**
 * Subscription Manager Component
 * 
 * Displays and manages user's current subscription.
 */

import React, { useState } from 'react';
import { 
  Crown, Calendar, AlertCircle, CheckCircle, 
  Settings, Loader2, X 
} from 'lucide-react';
import { 
  useActiveSubscription, 
  useCancelSubscription,
  useCreateCustomerPortal,
  useEntitlements,
  usePaymentCapabilities,
} from '../../hooks/usePayments';
import { 
  SubscriptionStatus,
  getSubscriptionStatusColor,
  getSubscriptionStatusLabel,
} from '../../types/payments';
import { useThemeStore } from '../../stores/themeStore';
import { PricingPage } from './PricingPage';

interface SubscriptionManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ isOpen, onClose }) => {
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';
  
  const { data: subscription, isLoading: isLoadingSubscription } = useActiveSubscription();
  const { data: entitlements, isLoading: isLoadingEntitlements } = useEntitlements();
  const { data: paymentCapabilities, isLoading: isLoadingCapabilities } = usePaymentCapabilities(isOpen);
  const cancelSubscription = useCancelSubscription();
  const createPortal = useCreateCustomerPortal();
  
  const [showPricing, setShowPricing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  if (!isOpen) return null;
  
  const isLoading = isLoadingSubscription || isLoadingEntitlements || isLoadingCapabilities;
  const hasActiveSubscription = entitlements?.has_active_subscription;
  const subscriptionsPublicEnabled = Boolean(paymentCapabilities?.subscriptions_public_enabled);
  const customerPortalPublicEnabled = Boolean(paymentCapabilities?.customer_portal_public_enabled);
  
  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    try {
      await cancelSubscription.mutateAsync({
        subscriptionId: subscription.id,
        data: { cancel_immediately: false },
      });
      setShowCancelConfirm(false);
    } catch (error) {
      console.error('Error canceling subscription:', error);
    }
  };
  
  const handleManageBilling = async () => {
    if (!customerPortalPublicEnabled) return;
    const returnUrl = window.location.href;
    createPortal.mutate(returnUrl);
  };
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className={`
          relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden
          ${isDark ? 'bg-slate-800' : 'bg-white'}
          shadow-2xl
        `}>
          {/* Header */}
          <div className={`
            p-6 border-b
            ${isDark ? 'border-white/10' : 'border-slate-200'}
          `}>
            <button
              onClick={onClose}
              className={`
                absolute top-4 right-4 p-2 rounded-full transition-colors
                ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
              `}
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                <Crown className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Subscription
                </h2>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Manage your subscription and billing
                </p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mb-4" />
                <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  Loading subscription...
                </p>
              </div>
            ) : !subscriptionsPublicEnabled && !customerPortalPublicEnabled ? (
              <div className="text-center py-8">
                <div className={`
                  w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center
                  ${isDark ? 'bg-slate-700' : 'bg-slate-100'}
                `}>
                  <Crown className={`w-8 h-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Subscription Billing Not Public Yet
                </h3>
                <p className={`mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Launch billing is currently limited to subject access through EcoCash.
                </p>
                <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  We&apos;ll enable subscription purchases again once the public billing rollout is ready.
                </p>
              </div>
            ) : hasActiveSubscription && subscription ? (
              <>
                {/* Current Plan */}
                <div className={`
                  p-4 rounded-xl mb-6
                  ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}
                `}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Current Plan
                      </p>
                      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {subscription.product_name || 'Premium'}
                      </h3>
                    </div>
                    <span className={`
                      px-3 py-1 rounded-full text-sm font-medium
                      ${getSubscriptionStatusColor(subscription.status as SubscriptionStatus)}
                      bg-current/10
                    `}>
                      {getSubscriptionStatusLabel(subscription.status as SubscriptionStatus)}
                    </span>
                  </div>
                  
                  {/* Billing Period */}
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                      {subscription.cancel_at_period_end ? (
                        <>Cancels on {formatDate(subscription.current_period_end)}</>
                      ) : (
                        <>Renews on {formatDate(subscription.current_period_end)}</>
                      )}
                    </span>
                  </div>
                </div>
                
                {/* Features */}
                {entitlements?.features && entitlements.features.length > 0 && (
                  <div className="mb-6">
                    <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Your Benefits
                    </h4>
                    <ul className="space-y-2">
                      {entitlements.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Actions */}
                <div className="space-y-3">
                  {customerPortalPublicEnabled && (
                    <button
                      onClick={handleManageBilling}
                      disabled={createPortal.isPending}
                      className={`
                        w-full py-3 px-4 rounded-xl font-medium transition-colors
                        flex items-center justify-center gap-2
                        ${isDark
                          ? 'bg-white/10 text-white hover:bg-white/20'
                          : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                        }
                      `}
                    >
                      {createPortal.isPending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Settings className="w-5 h-5" />
                          Manage Billing
                        </>
                      )}
                    </button>
                  )}
                  
                  {!subscription.cancel_at_period_end && (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full py-3 px-4 rounded-xl font-medium text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      Cancel Subscription
                    </button>
                  )}
                </div>
                
                {/* Cancel Confirmation */}
                {showCancelConfirm && (
                  <div className={`
                    mt-4 p-4 rounded-xl border
                    ${isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}
                  `}>
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          Cancel Subscription?
                        </h4>
                        <p className={`text-sm mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                          Your subscription will remain active until {formatDate(subscription.current_period_end)}.
                          You won't be charged again.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelSubscription}
                            disabled={cancelSubscription.isPending}
                            className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                          >
                            {cancelSubscription.isPending ? 'Canceling...' : 'Yes, Cancel'}
                          </button>
                          <button
                            onClick={() => setShowCancelConfirm(false)}
                            className={`
                              px-4 py-2 rounded-lg text-sm font-medium
                              ${isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}
                            `}
                          >
                            Keep Subscription
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* No Subscription */
              <div className="text-center py-8">
                <div className={`
                  w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center
                  ${isDark ? 'bg-slate-700' : 'bg-slate-100'}
                `}>
                  <Crown className={`w-8 h-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  No Active Subscription
                </h3>
                <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Upgrade to Premium for unlimited AI tutoring and more features.
                </p>
                {subscriptionsPublicEnabled ? (
                  <button
                    onClick={() => setShowPricing(true)}
                    className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-teal-500 hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                  >
                    View Plans
                  </button>
                ) : (
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    Public subscription purchases are disabled for the current launch.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Pricing Modal */}
      <PricingPage
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
      />
    </>
  );
};

export default SubscriptionManager;
